/**
 * Trip Cost Summary PDF Export
 * Generates a styled PDF with budget breakdown, expense list, and comparison data.
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const downloadBudgetPdf = async (itineraryId, itinerary, expenses, summary) => {
  const tripInput = itinerary?.trip_input || {};
  const budget = itinerary?.itinerary?.estimated_total_budget || {};
  const cities = tripInput.destination_cities?.length > 0
    ? tripInput.destination_cities.join(', ')
    : tripInput.destination_city || 'Unknown';

  // Fetch comparison data
  let comparison = null;
  try {
    const res = await fetch(`${API_URL}/api/itineraries/${itineraryId}/budget-comparison`);
    if (res.ok) comparison = await res.json();
  } catch { /* silent */ }

  // Build HTML content for the PDF
  const cleanVal = (v) => {
    if (!v) return 'N/A';
    return String(v).replace(/\s*\([^)]*\)/g, '').trim();
  };

  const categoryTotals = {};
  expenses.forEach(exp => {
    const cat = exp.category || 'other';
    categoryTotals[cat] = (categoryTotals[cat] || 0) + (exp.amount || 0);
  });

  const catLabels = {
    food: 'Food & Dining', flights: 'Flights', transport: 'Local Transport',
    accommodation: 'Accommodation', activities: 'Activities',
    shopping: 'Shopping', other: 'Other'
  };

  const sortedCats = Object.entries(categoryTotals).sort((a, b) => b[1] - a[1]);

  const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  let expenseRows = '';
  expenses.forEach((exp, i) => {
    const catInfo = catLabels[exp.category] || exp.category || 'Other';
    expenseRows += `
      <tr style="border-bottom:1px solid #e5e7eb;${i % 2 === 0 ? 'background:#f9fafb;' : ''}">
        <td style="padding:8px 12px;font-size:13px;">${exp.date || '-'}</td>
        <td style="padding:8px 12px;font-size:13px;">${catInfo}</td>
        <td style="padding:8px 12px;font-size:13px;">${exp.description || '-'}</td>
        <td style="padding:8px 12px;font-size:13px;text-align:right;font-weight:600;">${exp.currency || 'USD'} ${(exp.amount || 0).toFixed(2)}</td>
      </tr>`;
  });

  let comparisonSection = '';
  if (comparison && comparison.categories) {
    let compRows = '';
    comparison.categories.forEach(cat => {
      const diff = (cat.actual || 0) - (cat.estimated || 0);
      const diffColor = diff > 0 ? '#dc2626' : '#16a34a';
      const diffSign = diff > 0 ? '+' : '';
      compRows += `
        <tr style="border-bottom:1px solid #e5e7eb;">
          <td style="padding:8px 12px;font-size:13px;text-transform:capitalize;">${catLabels[cat.category] || cat.category}</td>
          <td style="padding:8px 12px;font-size:13px;text-align:right;">$${(cat.estimated || 0).toFixed(2)}</td>
          <td style="padding:8px 12px;font-size:13px;text-align:right;">$${(cat.actual || 0).toFixed(2)}</td>
          <td style="padding:8px 12px;font-size:13px;text-align:right;color:${diffColor};font-weight:600;">${diffSign}$${diff.toFixed(2)}</td>
        </tr>`;
    });

    comparisonSection = `
      <div style="margin-top:24px;page-break-inside:avoid;">
        <h2 style="font-size:18px;color:#0B3C5D;margin-bottom:12px;border-bottom:2px solid #d4a843;padding-bottom:6px;">Estimated vs Actual</h2>
        <table style="width:100%;border-collapse:collapse;border:1px solid #d1d5db;border-radius:8px;">
          <thead>
            <tr style="background:#0B3C5D;color:white;">
              <th style="padding:10px 12px;text-align:left;font-size:12px;">Category</th>
              <th style="padding:10px 12px;text-align:right;font-size:12px;">Estimated</th>
              <th style="padding:10px 12px;text-align:right;font-size:12px;">Actual</th>
              <th style="padding:10px 12px;text-align:right;font-size:12px;">Difference</th>
            </tr>
          </thead>
          <tbody>${compRows}</tbody>
        </table>
      </div>`;
  }

  const html = `
    <html>
    <head>
      <style>
        @page { margin: 40px; size: A4; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; color: #1f2937; line-height: 1.5; }
        .header { background: linear-gradient(135deg, #0B3C5D, #1a6b5a); color: white; padding: 24px 30px; border-radius: 12px; margin-bottom: 24px; }
        .header h1 { font-size: 24px; margin: 0 0 4px 0; }
        .header p { margin: 2px 0; opacity: 0.85; font-size: 14px; }
        .summary-grid { display: flex; gap: 12px; margin-bottom: 24px; }
        .summary-card { flex: 1; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 10px; padding: 16px; text-align: center; }
        .summary-card.warn { background: #fef3c7; border-color: #fde68a; }
        .summary-card .label { font-size: 11px; color: #6b7280; text-transform: uppercase; letter-spacing: 0.5px; }
        .summary-card .val { font-size: 22px; font-weight: 700; color: #0B3C5D; margin-top: 4px; }
        table { width: 100%; border-collapse: collapse; }
        .footer { margin-top: 30px; text-align: center; font-size: 11px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 12px; }
      </style>
    </head>
    <body>
      <div class="header">
        <h1>Trip Cost Summary</h1>
        <p>${cities}</p>
        <p>${tripInput.start_date || ''} to ${tripInput.end_date || ''} | ${tripInput.adults || 1} adult(s)${tripInput.kids ? `, ${tripInput.kids} kid(s)` : ''}</p>
      </div>

      <div class="summary-grid">
        <div class="summary-card">
          <div class="label">Total Spent</div>
          <div class="val">$${(summary?.total_spent || 0).toFixed(2)}</div>
        </div>
        <div class="summary-card">
          <div class="label">Expenses</div>
          <div class="val">${summary?.expense_count || 0}</div>
        </div>
        <div class="summary-card">
          <div class="label">Top Category</div>
          <div class="val" style="font-size:16px;text-transform:capitalize;">${sortedCats[0]?.[0]?.replace(/_/g, ' ') || '-'}</div>
        </div>
        <div class="summary-card ${(summary?.total_spent || 0) > 0 ? '' : ''}">
          <div class="label">Daily Average</div>
          <div class="val">$${summary?.expense_count > 0 ? (summary.total_spent / Math.max(1, new Set(expenses.map(e => e.date)).size)).toFixed(2) : '0.00'}</div>
        </div>
      </div>

      <div style="margin-bottom:24px;">
        <h2 style="font-size:18px;color:#0B3C5D;margin-bottom:12px;border-bottom:2px solid #d4a843;padding-bottom:6px;">AI Estimated Budget</h2>
        <div style="display:flex;gap:8px;flex-wrap:wrap;">
          ${budget.accommodation ? `<div style="background:#f0f9ff;border:1px solid #bae6fd;border-radius:8px;padding:10px 16px;flex:1;min-width:120px;"><div style="font-size:10px;color:#6b7280;text-transform:uppercase;">Accommodation</div><div style="font-weight:700;color:#0B3C5D;margin-top:2px;">${cleanVal(budget.accommodation)}</div></div>` : ''}
          ${budget.food ? `<div style="background:#f0f9ff;border:1px solid #bae6fd;border-radius:8px;padding:10px 16px;flex:1;min-width:120px;"><div style="font-size:10px;color:#6b7280;text-transform:uppercase;">Food</div><div style="font-weight:700;color:#0B3C5D;margin-top:2px;">${cleanVal(budget.food)}</div></div>` : ''}
          ${budget.flights ? `<div style="background:#f0f9ff;border:1px solid #bae6fd;border-radius:8px;padding:10px 16px;flex:1;min-width:120px;"><div style="font-size:10px;color:#6b7280;text-transform:uppercase;">Flights</div><div style="font-weight:700;color:#0B3C5D;margin-top:2px;">${cleanVal(budget.flights)}</div></div>` : ''}
          ${budget.transport ? `<div style="background:#f0f9ff;border:1px solid #bae6fd;border-radius:8px;padding:10px 16px;flex:1;min-width:120px;"><div style="font-size:10px;color:#6b7280;text-transform:uppercase;">Transport</div><div style="font-weight:700;color:#0B3C5D;margin-top:2px;">${cleanVal(budget.transport)}</div></div>` : ''}
          ${budget.activities ? `<div style="background:#f0f9ff;border:1px solid #bae6fd;border-radius:8px;padding:10px 16px;flex:1;min-width:120px;"><div style="font-size:10px;color:#6b7280;text-transform:uppercase;">Activities</div><div style="font-weight:700;color:#0B3C5D;margin-top:2px;">${cleanVal(budget.activities)}</div></div>` : ''}
          ${budget.total ? `<div style="background:#fef3c7;border:1px solid #fde68a;border-radius:8px;padding:10px 16px;flex:1;min-width:120px;"><div style="font-size:10px;color:#6b7280;text-transform:uppercase;font-weight:700;">Total</div><div style="font-weight:800;color:#0B3C5D;margin-top:2px;font-size:16px;">${cleanVal(budget.total)}</div></div>` : ''}
        </div>
      </div>

      ${comparisonSection}

      ${expenses.length > 0 ? `
      <div style="margin-top:24px;page-break-inside:avoid;">
        <h2 style="font-size:18px;color:#0B3C5D;margin-bottom:12px;border-bottom:2px solid #d4a843;padding-bottom:6px;">Expense Details (${expenses.length} items)</h2>
        <table style="border:1px solid #d1d5db;border-radius:8px;">
          <thead>
            <tr style="background:#0B3C5D;color:white;">
              <th style="padding:10px 12px;text-align:left;font-size:12px;">Date</th>
              <th style="padding:10px 12px;text-align:left;font-size:12px;">Category</th>
              <th style="padding:10px 12px;text-align:left;font-size:12px;">Description</th>
              <th style="padding:10px 12px;text-align:right;font-size:12px;">Amount</th>
            </tr>
          </thead>
          <tbody>${expenseRows}</tbody>
          <tfoot>
            <tr style="background:#f0fdf4;font-weight:700;">
              <td colspan="3" style="padding:10px 12px;font-size:14px;">Total</td>
              <td style="padding:10px 12px;text-align:right;font-size:14px;">$${(summary?.total_spent || 0).toFixed(2)}</td>
            </tr>
          </tfoot>
        </table>
      </div>
      ` : ''}

      <div class="footer">
        <p>Generated on ${now} | Triple A Travel Solutions | tripleabooking.com</p>
      </div>
    </body>
    </html>
  `;

  // Use print window to generate PDF
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    // Fallback for blocked popups
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trip-cost-summary-${cities.replace(/[^a-zA-Z0-9]/g, '-')}.html`;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.onload = () => {
    printWindow.print();
  };
};
